import {Component, EventEmitter, Input, Output} from '@angular/core';
import {NgClass} from '@angular/common';
import {Subheadline} from '@spirit-testing/component-library';
import {MatDivider} from '@angular/material/list';
import {MatCard, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {TranslatePipe} from '@ngx-translate/core';
import {AiDiffHunk, AiImproveSummary, HunkDecision} from '../ai-profile-matching.model';
import {ExportService} from '../../../services/export.service';
import {MessageService} from '../../../shared/components/message.service';

@Component({
  selector: 'app-improve-result',
  imports: [
    Subheadline,
    MatDivider,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatProgressSpinner,
    TranslatePipe,
    NgClass
  ],
  templateUrl: './improve-result.component.html',
  styleUrl: './improve-result.component.scss'
})
export class ImproveResultComponent {
  @Input() improvedProfile: string = '';
  @Input() hunks: AiDiffHunk[] = [];
  @Input() summary!: AiImproveSummary;
  @Input() isApplying: boolean = false;
  @Input() appliedProfile: string | null = null;
  @Input() consultantId: string | null = null;

  @Output() applyDecisions = new EventEmitter<Record<string, boolean>>();

  constructor(private exportService: ExportService, private messageService: MessageService) {
  }

  decisions: Record<number, HunkDecision> = {};

  get allHunksDecided(): boolean {
    if (this.hunks.length === 0) return false;
    return this.hunks.every(hunk => this.decisions[hunk.id] === 'accepted' || this.decisions[hunk.id] === 'rejected');
  }

  getDecision(hunkId: number): HunkDecision {
    return this.decisions[hunkId] ?? 'pending';
  }

  accept(hunkId: number): void {
    this.decisions[hunkId] = this.decisions[hunkId] === 'accepted' ? 'pending' : 'accepted';
  }

  reject(hunkId: number): void {
    this.decisions[hunkId] = this.decisions[hunkId] === 'rejected' ? 'pending' : 'rejected';
  }

  apply(): void {
    const booleanDecisions: Record<string, boolean> = {};
    for (const hunk of this.hunks) {
      booleanDecisions[String(hunk.id)] = this.decisions[hunk.id] === 'accepted';
    }
    this.applyDecisions.emit(booleanDecisions);
  }

  downloadProfile(): void {
    const text = this.appliedProfile ?? this.improvedProfile;
    const blob = new Blob([text], {type: 'text/plain'});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'improved_profile.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  }

  exportProfile() {
    this.messageService.info('aiProfileMatching.startExport');
    this.exportService.exportAI(this.consultantId, this.appliedProfile ?? this.improvedProfile).subscribe({
      next: () => {
        this.messageService.success('aiProfileMatching.finishExport');
      }, error: (err) => this.showErrorWithDetail(err, 'aiProfileMatching.errorMessages.exportFailed')
    })
  }

  private showErrorWithDetail(err: any, messageKey: string): void {
    const body = err?.error;
    const violations: Record<string, string> | undefined = body?.parameters?.violations;
    const detail = violations && Object.keys(violations).length > 0
      ? Object.values(violations).join(', ')
      : body?.message;
    if (detail) {
      this.messageService.errorWithDetail(messageKey, detail);
    } else {
      this.messageService.error(messageKey);
    }
  }
}
